import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  ClientDictionaryClient,
  SearchClientsDictionaryRequest,
  SearchClientsDictionaryResponsePagedList,
} from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { IDictionaryItem } from '../../types/dictionary-item.interface';

interface IClientsRequest extends SearchClientsDictionaryRequest {
  skip: number;
  take: number;
  includeTotalCount: boolean;
}

@Injectable()
export class ClientDictionaryService {
  private readonly clientDictionaryClient = inject(ClientDictionaryClient);
  private readonly toastService = inject(ToastService);
  private readonly skipCount = 10 as const;

  public readonly items: WritableSignal<IDictionaryItem<string>[]> = signal<
    IDictionaryItem<string>[]
  >([]);
  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public readonly hasLoaded: WritableSignal<boolean> = signal<boolean>(false);

  private currentLoadToken = 0;

  private request: IClientsRequest = {
    skip: 0,
    take: this.skipCount,
    includeTotalCount: true,
  };

  public search(keyword: string): void {
    this.request = {
      ...this.request,
      skip: 0,
      keyword,
    };

    this.items.set([]);

    this.loadPage();
  }

  public loadMore(): void {
    if (this.isLoading()) return;

    if (this.items().length >= this.totalCount()) return;

    this.request = {
      ...this.request,
      skip: this.request.skip + this.skipCount,
    };

    this.loadPage();
  }

  private loadPage(): void {
    const request = { ...this.request };
    const loadToken = ++this.currentLoadToken;

    this.isLoading.set(true);
    this.hasLoaded.set(false);

    this.clientDictionaryClient.v1ClientDictionarySearchPost(request).subscribe({
      next: (response: SearchClientsDictionaryResponsePagedList): void => {
        if (this.shouldIgnoreLoadResult(loadToken)) return;

        this.applyResponse(response, request);
      },
      error: (err: unknown): void => {
        if (this.shouldIgnoreLoadResult(loadToken)) return;

        this.handleLoadError(err, request);
      },
    });
  }

  private applyResponse(
    response: SearchClientsDictionaryResponsePagedList,
    request: IClientsRequest,
  ): void {
    const nextItems = this.mapItems(response.items);

    this.totalCount.set(response.totalCount ?? 0);
    this.items.update((currentItems): IDictionaryItem<string>[] =>
      request.skip === 0 ? nextItems : this.mergeItems(currentItems, nextItems),
    );
    this.isLoading.set(false);
    this.hasLoaded.set(true);
  }

  private handleLoadError(err: unknown, request: IClientsRequest): void {
    this.request = {
      ...this.request,
      skip: Math.max(request.skip - this.skipCount, 0),
    };

    this.toastService.error({
      message: 'monitoring.selector.toast.clients-fetching-error',
    });

    console.error('Error fetching client dictionary items:', err);

    this.isLoading.set(false);
    this.hasLoaded.set(true);
  }

  private mapItems(
    items: SearchClientsDictionaryResponsePagedList['items'],
  ): IDictionaryItem<string>[] {
    if (!Array.isArray(items)) return [];

    return items
      .filter((item): boolean => item.id !== undefined && item.id.length > 0)
      .map(
        (item): IDictionaryItem<string> => ({
          id: item.id,
          name: item.name ?? '-',
        }),
      );
  }

  private mergeItems(
    currentItems: IDictionaryItem<string>[],
    nextItems: IDictionaryItem<string>[],
  ): IDictionaryItem<string>[] {
    const itemsById = new Map(
      currentItems.map((item): [string, IDictionaryItem<string>] => [item.id, item]),
    );

    nextItems.forEach((item): void => {
      itemsById.set(item.id, item);
    });

    return [...itemsById.values()];
  }

  private shouldIgnoreLoadResult(loadToken: number): boolean {
    return loadToken !== this.currentLoadToken;
  }
}
